import { SearchBox } from '@mapbox/search-js-react';
import React, { useState, useEffect } from 'react';
import { MAP_BOX_KEY } from '../../../config/config';

export interface AddressComponents {
  addressLine1?: string;
  addressLine2?: string;
  town?: string;
  postalCode?: string;
  region?: string;
  country?: string;
}

interface MapboxAutocompleteProps {
  fieldId: string;
  fieldLabel: string;
  value: string;
  onChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => void;
  onLocationSelect?: (
    latitude: number,
    longitude: number,
    addressComponents?: AddressComponents
  ) => void;
  placeholder?: string;
  required?: boolean;
  error?: string;
}

const MapboxAutocomplete: React.FC<MapboxAutocompleteProps> = ({
  fieldId,
  fieldLabel,
  value,
  onChange,
  onLocationSelect,
  placeholder,
  required,
  error,
}) => {
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const extractCoordinates = (
    feature: any,
  ): { latitude: number; longitude: number } | null => {
    const geometry = feature?.geometry?.coordinates;
    if (
      Array.isArray(geometry) &&
      geometry.length >= 2 &&
      typeof geometry[0] === 'number' &&
      typeof geometry[1] === 'number'
    ) {
      return { longitude: geometry[0], latitude: geometry[1] };
    }

    const propertyCoordinates = feature?.properties?.coordinates;
    if (
      propertyCoordinates &&
      typeof propertyCoordinates.longitude === 'number' &&
      typeof propertyCoordinates.latitude === 'number'
    ) {
      return {
        longitude: propertyCoordinates.longitude,
        latitude: propertyCoordinates.latitude,
      };
    }

    const center = feature?.center;
    if (
      Array.isArray(center) &&
      center.length >= 2 &&
      typeof center[0] === 'number' &&
      typeof center[1] === 'number'
    ) {
      return { longitude: center[0], latitude: center[1] };
    }

    return null;
  };

  const extractAddressComponents = (feature: any): AddressComponents => {
    const props = feature?.properties || {};
    const context = feature?.properties?.context || feature?.context || {};

    // Address line 1 - street address from properties
    const addressLine1 = props.address || props.name_preferred || props.address_line1 || '';

    // Address line 2 - neighborhood from context
    const addressLine2 = context.neighborhood?.name || '';

    // Town/City - from place context
    const town = context.place?.name || '';

    // Postal code - from postcode context
    const postalCode = context.postcode?.name || '';

    // Region/State - from region context
    const region = context.region?.name || '';

    // Country - from country context
    const country = context.country?.name || '';

    const components = {
      addressLine1,
      addressLine2,
      town,
      postalCode,
      region,
      country,
    };

    return components;
  };

  const geocodeAddress = async (
    address: string,
  ): Promise<{ latitude: number; longitude: number } | null> => {
    if (!address.trim() || !MAP_BOX_KEY) {
      return null;
    }

    try {
      const url = new URL(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json`,
      );
      url.searchParams.set('country', 'ZA');
      url.searchParams.set('limit', '1');
      url.searchParams.set('access_token', MAP_BOX_KEY);

      const response = await fetch(url.toString());
      if (!response.ok) {
        return null;
      }

      const data = await response.json();
      const firstFeature = data?.features?.[0];
      const center = firstFeature?.center;

      if (
        Array.isArray(center) &&
        center.length >= 2 &&
        typeof center[0] === 'number' &&
        typeof center[1] === 'number'
      ) {
        return { longitude: center[0], latitude: center[1] };
      }
    } catch {
      return null;
    }

    return null;
  };

  const handleRetrieve = async (res: any) => {
    const feature = res?.features?.[0] ?? res?.detail?.features?.[0];

    if (feature) {
      const selectedAddress =
        feature?.properties?.full_address ||
        feature?.properties?.place_formatted ||
        feature?.place_name ||
        feature?.properties?.name ||
        feature?.name ||
        feature?.text ||
        '';

      if (selectedAddress) {
        const syntheticEvent = {
          target: {
            id: fieldId,
            value: selectedAddress,
            type: 'text',
          },
        } as unknown as React.ChangeEvent<HTMLInputElement>;

        onChange(syntheticEvent);
      }

      const directCoordinates = extractCoordinates(feature);
      const fallbackCoordinates =
        directCoordinates ?? (selectedAddress ? await geocodeAddress(selectedAddress) : null);

      if (fallbackCoordinates) {
        const addressComponents = extractAddressComponents(feature);
        onLocationSelect?.(
          fallbackCoordinates.latitude,
          fallbackCoordinates.longitude,
          addressComponents
        );
      }
    }
  };

  return (
    <div className="mb-6 text-left">
      <label
        htmlFor={fieldId}
        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
      >
        {fieldLabel}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {error && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
      <style>
        {`
          input[class*='Input'] {
            height: 40px !important;
            font-size: 14px !important;
            padding-left: 40px !important;
            background-color: #ffffff !important;
          }
          div[class*='Suggestion'] {
            font-size: 14px !important;
          }
          div[class*='Results'] {
            font-size: 14px !important;
          }
        `}
      </style>
      {/* @ts-ignore - SearchBox type issues */}
      <SearchBox
        accessToken={MAP_BOX_KEY || ''}
        value={inputValue}
        options={{
          country: 'ZA',
          language: 'en',
        }}
        placeholder={placeholder || 'Search for an address or place'}
        onRetrieve={handleRetrieve}
        onChange={(value: string) => {
          setInputValue(value);
          const syntheticEvent = {
            target: {
              id: fieldId,
              value: value,
              type: 'text',
            },
          } as unknown as React.ChangeEvent<HTMLInputElement>;
          onChange(syntheticEvent);
        }}
        theme={{
          variables: {
            fontFamily: 'inherit',
            unit: '10px',
            padding: '0.625em',
            borderRadius: '0.25rem',
            border: '1px solid rgb(226, 232, 240)',
            colorText: 'rgb(100, 116, 139)',
            colorBackground: '#ffffff',
          },
        }}
      />
    </div>
  );
};

export default MapboxAutocomplete;
